import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, delay, catchError, switchMap } from 'rxjs/operators';
import { API_BASE_URL, API_ROUTES } from '@core/constants/api.constants';
import { Router } from '@angular/router';
import { LoginRequest, LoginResponse, Permission } from '../models/auth.model';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // URL completa para el endpoint de login
  private readonly loginUrl = `${API_BASE_URL}${API_ROUTES.AUTH.LOGIN}`;
  private readonly permissionsUrl = `${environment.apiBaseUrl}/users/me/permissions`;

  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  /**
   * Inicia sesión en el sistema
   * @param email Correo electrónico
   * @param password Contraseña
   * @returns Observable con los datos del usuario y token
   */
  login(email: string, password: string): Observable<LoginResponse> {
    const loginData: LoginRequest = { email, password };
    
    return this.http.post<LoginResponse>(this.loginUrl, loginData).pipe(
      tap(response => {
        if (!response.error) {
          // Guardamos el token en localStorage
          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('token', response.access_token); // Mantener compatibilidad
          localStorage.setItem('user', JSON.stringify(response.user));
          }
      }),
      switchMap(response => {
        // Después del login exitoso, cargar los permisos del usuario
        if (!response.error && this.isAdminUser()) {
          return this.loadUserPermissions().pipe(
            tap(permissions => {
              // Actualizar el usuario con los permisos
              const user = response.user;
              user.permissions = permissions;
              localStorage.setItem('user', JSON.stringify(user));
              }),
            switchMap(() => of(response)),
            catchError(error => {
              return of(response);
            })
          );
        }
        return of(response);
      }),
      catchError(error => {
        console.error('❌ Error durante el login:', error);
        console.error('📊 Status:', error.status);
        console.error('📝 Message:', error.message);
        console.error('🔍 Error completo:', JSON.stringify(error, null, 2));
        throw error;
      })
    );
  }

  /**
   * Carga los permisos del usuario actual desde el backend
   */
  loadUserPermissions(): Observable<Permission[]> {
    const headers = this.getAuthHeaders();
    return this.http.get<Permission[]>(this.permissionsUrl, { headers });
  }

  /**
   * Obtiene los headers de autenticación
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  /**
   * Cierra la sesión del usuario
   * @returns Observable para confirmar cierre de sesión
   */
  logout(): Observable<any> {
    // Eliminamos los datos de sesión
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Redirigimos al login
    this.router.navigate(['/login']);
    
    // Simulamos una respuesta exitosa
    return of({ success: true }).pipe(delay(300));
    
    // Código para cuando tengamos el endpoint real
    /*
    return this.http.post(`${environment.apiUrl}${this.API_URLS.LOGOUT}`, {}).pipe(
      tap(() => {
        // Eliminamos los datos de sesión
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Redirigimos al login
        this.router.navigate(['/login']);
      })
    );
    */
  }

  /**
   * Verifica si el usuario está autenticado
   * @returns boolean
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    // Aquí podríamos añadir lógica adicional para verificar si el token ha expirado
    // basándonos en la información guardada en el token o en el usuario
    
    return true;
  }

  /**
   * Obtiene el usuario actual
   * @returns datos del usuario o null
   */
  getCurrentUser(): any {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  /**
   * Verifica si el usuario actual tiene permisos de administrador
   * @returns boolean
   */
  isAdminUser(): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.role) return false;
    
    // Permitir acceso a todos los roles excepto 'customer'
    return user.role.toLowerCase() !== 'customer';
  }

  /**
   * Obtiene los permisos del usuario actual
   * @returns array de permisos o array vacío
   */
  getUserPermissions(): Permission[] {
    const user = this.getCurrentUser();
    return user?.permissions || [];
  }

  /**
   * Verifica si el usuario tiene un permiso específico por código
   * @param permissionCode código del permiso a verificar
   * @returns boolean
   */
  hasPermission(permissionCode: string): boolean {
    const permissions = this.getUserPermissions();
    return permissions.some(p => p.code === permissionCode && p.is_active);
  }

  /**
   * Obtiene el rol del usuario actual
   * @returns string | null
   */
  getUserRole(): string | null {
    const user = this.getCurrentUser();
    return user?.role || null;
  }

  /**
   * Obtiene el perfil del usuario desde el servidor
   * @returns Observable con los datos del perfil
   */
  getProfile(): Observable<any> {
    // Simulamos datos del perfil
    const mockProfile = {
      id: 1,
      username: 'admin',
      fullName: 'Sebastián',
      email: 'admin@mapoh.com',
      role: 'ADMIN',
      lastLogin: '2025-06-10T12:30:00'
    };
    
    return of(mockProfile).pipe(delay(500));
    
    // Código para cuando tengamos el endpoint real
    // return this.http.get(`${environment.apiUrl}${this.API_URLS.PROFILE}`);
  }
}
